import axios, { type AxiosError, type AxiosRequestConfig } from 'axios'
import { ElMessage } from 'element-plus'

/**
 * 统一的 axios 封装工厂。
 *
 * admin (request.ts) 与 portal/mp (request-mp.ts) 两套请求器 ~90% 相同,
 * 差异仅在 baseURL、取哪个 store 的 token、鉴权失败后跳哪个登录页,
 * 以及 403 的处理策略。此工厂把共性收敛, 差异通过 options 注入,
 * 两个入口文件退化为薄配置。
 *
 * ⚠️ 行为差异被显式保留 (勿"顺手统一"):
 * - admin: 封套 code===403 走通用错误提示(不登出); http 403 仅提示"权限不足"。
 * - portal: 封套 code===403 等同鉴权失败(登出+跳登录); http 403 同样登出跳转。
 */
export interface RequestFactoryOptions {
  baseURL: string
  /** 每次请求取最新 token (Pinia store 须在调用时求值) */
  getToken: () => string | null | undefined
  /** 清除登录态 */
  logout: () => void
  /** 鉴权失败后按当前所在域决定是否跳登录页 (admin / portal 各异) */
  redirectToLogin: () => void
  /** 封套 code===403 / http 403 是否等同鉴权失败。admin=false, portal=true */
  treat403AsAuthFailure: boolean
  /** 非 2xx 且非 401/403 时的兜底提示文案 (两域取值来源不同) */
  networkErrorMessage: (error: AxiosError) => string
}

export interface RequestFn {
  <T = unknown>(config: AxiosRequestConfig): Promise<T>
  <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T>
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>
  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>
  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) => Promise<T>
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) => Promise<T>
}

export function createRequest(opts: RequestFactoryOptions): RequestFn {
  const service = axios.create({
    baseURL: opts.baseURL,
    timeout: 15000
  })

  service.interceptors.request.use(
    (config) => {
      const token = opts.getToken()
      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  /**
   * 集中式鉴权失败处理: 提示 + 登出 + (按域) 跳登录页。
   * 不整页刷新, 保留用户当前上下文 (打开的弹窗、滚动、表单)。
   */
  function handleAuthFailure(message: string) {
    ElMessage({ message, type: 'error', duration: 3 * 1000 })
    opts.logout()
    opts.redirectToLogin()
  }

  service.interceptors.response.use(
    (response) => {
      const res = response.data
      // Backend standard envelope: { code: 0, message: "ok", data: T }
      // See AGENTS.md §H5. code === 0 means success.
      if (res && typeof res === 'object' && 'code' in res) {
        if (res.code !== 0) {
          if (res.code === 401 || (opts.treat403AsAuthFailure && res.code === 403)) {
            handleAuthFailure(res.message || '登录已过期，请重新登录')
          } else {
            // Do not show default ElMessage if need_voucher is present, let the component handle it
            if (!res.data?.need_voucher) {
              ElMessage({ message: res.message || 'Error', type: 'error', duration: 5 * 1000 })
            }
          }
          const error: Error & { code?: number, data?: unknown } = new Error(res.message || `Error code: ${res.code}`)
          error.code = res.code
          error.data = res.data
          return Promise.reject(error)
        }
        return res.data
      }
      return res
    },
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        handleAuthFailure('登录已过期，请重新登录')
      } else if (error.response?.status === 403) {
        if (opts.treat403AsAuthFailure) {
          handleAuthFailure('权限不足')
        } else {
          ElMessage({ message: '权限不足', type: 'error', duration: 3 * 1000 })
        }
      } else {
        ElMessage({
          message: opts.networkErrorMessage(error),
          type: 'error',
          duration: 5 * 1000
        })
      }
      return Promise.reject(error)
    }
  )

  /**
   * 强类型 wrapper。拦截器已把响应解包为 res.data (无壳时为原始 payload),
   * 因此调用方直接拿到 T, 而非 AxiosResponse。
   *
   * 支持两种形态: request(config) 与 request(url, config)。
   */
  const request = (<T = unknown>(
    configOrUrl: AxiosRequestConfig | string,
    maybeConfig?: AxiosRequestConfig
  ): Promise<T> => {
    if (typeof configOrUrl === 'string') {
      return service.request({ ...maybeConfig, url: configOrUrl }) as unknown as Promise<T>
    }
    return service.request(configOrUrl) as unknown as Promise<T>
  }) as RequestFn

  request.get = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    request<T>({ ...config, method: 'GET', url })

  request.post = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    request<T>({ ...config, method: 'POST', url, data })

  request.put = <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> =>
    request<T>({ ...config, method: 'PUT', url, data })

  request.delete = <T = unknown>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    request<T>({ ...config, method: 'DELETE', url })

  return request
}
